<template>
  <VDialog
    :open="isOpen"
    headline="Custom data and tags"
    modal
    full-width-body
    @close="$emit('dismiss')"
    style="--dialog-min-inline-size: 600px; --dialog-max-inline-size: 900px;"
  >
    <template #body>
      <!-- Loading state -->
      <div v-if="loading" class="loading-state">
        <VProgressRing connotation="cta" :size="-3" />
        <p class="sub-title">Loading...</p>
      </div>

      <!-- Tabs -->
      <VTabs v-else :activeid="activeTab" connotation="cta" tabs-layout="stretch" @change="(e) => $emit('update:activeTab', e.target?.id || e.detail?.id)">
        <VTab id="customData" label="Custom Data"></VTab>
        <VTab id="tags" label="Tags"></VTab>
        <VTabPanel>
          <!-- Custom data tab -->
          <div>
            <!-- <div class="marginBottom">
              <div class="warning-info-box">
                <VIcon name="error-line" class="warningIcon" />
                <p class="info-text">
                  The maximum number of custom data allowed per 'Account' is 240. If the limit is reached, remove a custom data to add a new one.
                </p>
              </div>
            </div> -->
            <div class="flex-between">
              <div class="flex-start-container gap10P">
                <VIcon name="inbox-3-line" class="iconPrimary" />
                <h2 class="sub-title no-margin">Custom data</h2>
              </div>
              <div class="flex-start">
                <p class="sub-header">{{ total.customFields }}</p>
                <!-- <HelpTooltip v-if="total.customFields === 240" :iconStyle="{ color: '#FA7554' }">
                  Limit Reached
                </HelpTooltip> -->
              </div>
            </div>
            <div class="fields-container">
              <div class="table-header-row">
                <div class="col-left">
                  <div class="custom-field-search-wrapper">
                    <SearchBar
                      v-model="fieldsSearchTerm"
                      placeholder="Search custom fields..."
                      custom-class="fields-searchbar"
                      :show-no-results="true"
                      :no-results-message="`No custom fields found matching &quot;${fieldsSearchTerm}&quot;`"
                      @search-input="onFieldsSearchInput"
                    />
                  </div>
                </div>
                <div class="col-right">
                  <p class="vtext-subtitle"><b>Action</b></p>
                </div>
              </div>
              <div class="rows-scroll-area">
                <div v-for="field in visibleFields" :key="field" class="table-row">
                  <div class="col-left">
                    <p class="vtext-body">{{ field }}</p>
                  </div>
                  <div class="col-right">
                    <VIcon @click.stop="fieldToDelete = field" name="delete-line" connotation="alert" class="trashIcon searchIcon cursorPointer" />
                  </div>
                </div>
                <div v-if="filteredFields.length > fieldsVisibleCount" class="load-more-container">
                  <button class="btn-load-more" @click="fieldsVisibleCount += 30">
                    Load more ({{ filteredFields.length - fieldsVisibleCount }} remaining)
                  </button>
                </div>
                <div v-if="filteredFields.length === 0 && fieldsSearchTerm" class="empty-search-state">
                  <VEmptyState icon="search-line" />
                  <p class="sub-title">No custom fields found matching "{{ fieldsSearchTerm }}"</p>
                </div>
              </div>
            </div>
          </div>
        </VTabPanel>
        <VTabPanel>
          <!-- Tags tab -->
          <div>
            <!-- <div class="marginBottom">
              <div class="warning-info-box">
                <VIcon name="error-line" class="warningIcon" />
                <p class="info-text">
                  The maximum number of unique tags allowed per 'Account' is 60. If the limit is reached, remove a tag to add a new one.
                </p>
              </div>
            </div> -->
            <div class="flex-between">
              <div class="flex-start-container gap10P">
                <VIcon name="tag-line" class="iconPrimary" />
                <h2 class="sub-title no-margin">Custom tags</h2>
              </div>
              <div class="flex-start">
                <p class="sub-header">{{ total.tags }}</p>
                <!-- <HelpTooltip v-if="total.tags === 60" :iconStyle="{ color: '#FA7554' }">
                  Limit Reached
                </HelpTooltip> -->
              </div>
            </div>
            <div class="fields-container">
              <div class="table-header-row">
                <div class="col-left">
                  <div class="custom-field-search-wrapper">
                    <SearchBar
                      v-model="tagsSearchTerm"
                      placeholder="Search tags..."
                      custom-class="tags-search-bar"
                      clear-icon
                      @search-input="onTagsSearchInput"
                    />
                  </div>
                </div>
                <div class="col-right">
                  <p class="vtext-subtitle"><b>Action</b></p>
                </div>
              </div>
              <div class="rows-scroll-area">
                <div v-for="tag in visibleTags" :key="tag" class="table-row">
                  <div class="col-left">
                    <p class="vtext-body">{{ tag }}</p>
                  </div>
                  <div class="col-right">
                    <VIcon @click.stop="tagToDelete = tag" name="delete-line" connotation="alert" class="trashIcon searchIcon cursorPointer" />
                  </div>
                </div>
                <div v-if="filteredTags.length > tagsVisibleCount" class="load-more-container">
                  <button class="btn-load-more" @click="tagsVisibleCount += 30">
                    Load more ({{ filteredTags.length - tagsVisibleCount }} remaining)
                  </button>
                </div>
                <div v-if="filteredTags.length === 0 && tagsSearchTerm" class="empty-search-state">
                  <VEmptyState icon="search-line" />
                  <p class="sub-title">No tags found matching "{{ tagsSearchTerm }}"</p>
                </div>
              </div>
            </div>
          </div>
        </VTabPanel>
      </VTabs>
    </template>
  </VDialog>

  <!-- Confirm delete dialog -->
  <VDialog
    :open="!!(tagToDelete || fieldToDelete)"
    headline="Are you sure?"
    modal
    @close="tagToDelete = undefined; fieldToDelete = undefined"
  >
    <template #body>
      <p class="vtext-body">You want to delete '{{ tagToDelete || fieldToDelete }}' {{ tagToDelete ? 'tag' : 'custom data' }}?</p>
    </template>
    <template #action-items>
      <VButton class="vvd-btn-cancel" appearance="filled" label="Cancel" @click="tagToDelete = undefined; fieldToDelete = undefined"></VButton>
      <VButton class="vvd-btn-primary" connotation="cta" appearance="filled" label="Yes" @click="confirmDelete"></VButton>
    </template>
  </VDialog>
</template>

<script>
import HelpTooltip from '@/components/HelpTooltip.vue';
import SearchBar from '@/components/SearchBar.vue';

export default {
  name: 'CustomDataTagsModal',
  components: { HelpTooltip, SearchBar },
  props: {
    isOpen: { type: Boolean, required: true },
    allFields: { type: Array, default: () => [] },
    allTags: { type: Array, default: () => [] },
    total: { type: Object, default: () => ({ customFields: 0, tags: 0 }) },
    activeTab: { type: String, default: 'customData' },
    loading: { type: Boolean, default: false },
  },
  emits: ['dismiss', 'delete-tag', 'delete-field', 'update:activeTab'],
  data() {
    return {
      fieldsSearchTerm: '',
      tagsSearchTerm: '',
      tagToDelete: undefined,
      fieldToDelete: undefined,
      fieldsVisibleCount: 30,
      tagsVisibleCount: 30,
    };
  },
  computed: {
    filteredFields() {
      if (!this.fieldsSearchTerm) return this.allFields;
      return this.allFields.filter(f => f.toLowerCase().includes(this.fieldsSearchTerm.toLowerCase()));
    },
    filteredTags() {
      if (!this.tagsSearchTerm) return this.allTags;
      return this.allTags.filter(t => t.toLowerCase().includes(this.tagsSearchTerm.toLowerCase()));
    },
    visibleFields() {
      return this.filteredFields.slice(0, this.fieldsVisibleCount);
    },
    visibleTags() {
      return this.filteredTags.slice(0, this.tagsVisibleCount);
    },
  },
  watch: {
    isOpen(val) {
      if (!val) {
        this.fieldsSearchTerm = '';
        this.tagsSearchTerm = '';
        this.fieldsVisibleCount = 30;
        this.tagsVisibleCount = 30;
      }
    },
    fieldsSearchTerm() {
      this.fieldsVisibleCount = 30;
    },
    tagsSearchTerm() {
      this.tagsVisibleCount = 30;
    },
  },
  methods: {
    confirmDelete() {
      const tag = this.tagToDelete;
      const field = this.fieldToDelete;
      this.tagToDelete = undefined;
      this.fieldToDelete = undefined;
      // Emit delete first, then dismiss so parent can handle the API call
      if (tag) {
        this.$emit('delete-tag', tag);
      } else if (field) {
        this.$emit('delete-field', field);
      }
      this.$emit('dismiss');
    },
    onFieldsSearchInput(payload) {
      this.fieldsSearchTerm = typeof payload === 'string' ? payload : (payload?.value ?? '');
    },
    onTagsSearchInput(payload) {
      this.tagsSearchTerm = typeof payload === 'string' ? payload : (payload?.value ?? '');
    },
  },
};
</script>

<style scoped>
/* Table layout */
.fields-container {
  margin: 10px 0;
}
/* Fixed-height scroll area so the modal doesn't resize as the number of
   search results changes. Only the rows scroll; header stays put. */
.rows-scroll-area {
  height: 360px;
  overflow-y: auto;
}
.rows-scroll-area::-webkit-scrollbar {
  width: 4px;
}
.rows-scroll-area::-webkit-scrollbar-thumb {
  background: var(--gray-04);
  border-radius: 2px;
}
.table-header-row,
.table-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: 20px 0;
  border-bottom: 1px solid var(--background-primary);
}
.table-header-row {
  padding: 10px 0 10px 0;
}
.col-left {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
}
.col-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.warning-info-box {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--warning);
  padding: 10px;
  border-radius: 5px;
}
.info-text {
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  color: var(--black);
  margin: 0;
}
.sub-header {
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 16px;
  color: var(--black);
  margin: 0;
}
.flex-start-container {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}
.custom-field-search-wrapper {
  width: 70%;
}
.marginBottom {
  margin-bottom: 20px;
}
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  padding: 40px;
  gap: 16px;
}
.empty-search-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
}
.no-margin {
  margin: 0px;
}
.load-more-container {
  display: flex;
  justify-content: center;
  padding: 16px 0;
}
.btn-load-more {
  background: transparent;
  border: 1px solid var(--purple);
  color: var(--purple);
  padding: 8px 20px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 13px;
  transition: background 0.2s;
}
.btn-load-more:hover {
  background: var(--purple-opac);
}
</style>
